import React from "react";

import { useApolloClient } from "@apollo/client";

import { RadioButton } from "primereact/radiobutton";
import { DataTable } from "primereact/datatable";
import { InputText } from "primereact/inputtext";
import { classNames } from "primereact/utils";
import { Toolbar } from "primereact/toolbar";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { Toast } from "primereact/toast";

/** contexts */
import { useSettings } from "../../context/settings";

/** QUERIES MUTATIONS */
import CREATE_USER, { UPDATE_USER } from "./../../provider/Users/mutation";
import { USERS } from "../../provider/Users/query";

/** utils */
import { getGraphsqErros, maskPhone } from "../../utilities/functions";

const Crud = () => {
    const settings = useSettings();
    const apollo = useApolloClient();

    let emptyRecord: iUser = {
        name: "",
        phone: "",
        email: "",
        role: "DOCTOR",
        password: "",
    };

    const [records, setRecords] = React.useState<any[]>([]);
    const [record, setRecord] = React.useState<iUser>();

    const [loading, setLoading] = React.useState<boolean>(false);
    const [recordDialog, setRecordDialog] = React.useState<boolean>(false);

    const [submitted, setSubmitted] = React.useState<boolean>(false);
    const [globalFilter, setGlobalFilter] = React.useState<any>(null);
    const toast = React.useRef<any>(null);
    const dt = React.useRef<any>(null);

    const loadRecords = (cleanRecord: boolean = false) => {
        setLoading(true);

        apollo
            .query({
                query: USERS,
                variables: {},
                fetchPolicy: "no-cache",
            })
            .then(response => {
                const data = response.data.users;
                if (data) {
                    setRecords(data);
                }
                setLoading(false);
            })
            .catch(error => {
                setRecords([]);
                settings?.setErrorMessage(getGraphsqErros(error));
                settings?.setVisibleError(true);
                setLoading(false);
            });
    };

    const hideDialog = () => {
        setSubmitted(false);
        setRecordDialog(false);
    };

    const saveRecord = () => {
        setSubmitted(true);

        if (!record?.name || !record.phone) return;

        const _record: iUser = { ...record };
        delete _record.__typename;
        delete _record.email;
        delete _record.token;
        delete _record.token_expires_at;
        delete _record.push_token;

        setLoading(true);

        let mutation = CREATE_USER;

        if (record?.id) {
            _record.id = parseInt(`${_record.id}`);
            mutation = UPDATE_USER;
        }

        apollo
            .mutate({
                mutation: mutation,
                variables: {
                    data: {
                        ..._record,
                    },
                },
            })
            .then(response => {
                setLoading(false);

                toast.current.show({ severity: "success", summary: "Sucesso", detail: "Registro atualizado.", life: 3000 });

                setRecordDialog(false);
                setRecord(emptyRecord);
                loadRecords(true);
            })
            .catch(error => {
                setLoading(false);
                settings.setErrorMessage(getGraphsqErros(error));
                settings.setVisibleError(true);
            });
    };

    const editRecord = (record: iUser) => {
        setRecord({ ...record });
        setRecordDialog(true);
    };

    const openNew = () => {
        setRecord(emptyRecord);
        setSubmitted(false);
        setRecordDialog(true);
    };

    const onInputChange = (e: any, name: string) => {
        let val = (e.target && e.target.value) || "";

        if (name === "phone") {
            val = maskPhone(val);
        }

        let _record: any = { ...record };
        _record[name] = val;

        setRecord(_record);
    };

    const onRoleChange = (e: any) => {
        let _record: iUser = { ...record };
        _record.role = e.value;
        setRecord(_record);
    };

    const codeBodyTemplate = (rowData: iUser) => {
        return (
            <>
                <span className="p-column-title">Uid</span>
                {rowData.id}
            </>
        );
    };

    const nameBodyTemplate = (rowData: iUser) => {
        return (
            <>
                <span className="p-column-title">Nome</span>
                {rowData.name}
            </>
        );
    };

    const phoneBodyTemplate = (rowData: iUser) => {
        return (
            <>
                <span className="p-column-title">Telefone</span>
                {rowData.phone}
            </>
        );
    };

    const adminBodyTemplate = (rowData: iUser) => {
        return (
            <>
                <span className="p-column-title">Admin</span>
                <span className={`product-badge status-actived`}>{rowData.role}</span>
            </>
        );
    };

    const actionBodyTemplate = (rowData: any) => {
        return (
            <div className="actions">
                <Button icon="pi pi-pencil" className="p-button-rounded p-button-info mr-2" onClick={() => editRecord(rowData)} />
            </div>
        );
    };

    const leftToolbarTemplate = () => {
        return (
            <React.Fragment>
                <Button label="Novo" icon="pi pi-plus" className="p-button-success mr-2 " onClick={openNew} />
                {/* <Button
                    label="Delete"
                    icon="pi pi-trash"
                    className="p-button-danger mb-2"
                    onClick={confirmDeleteSelected}
                    disabled={!selectedRecords || !selectedRecords.length}
                /> */}
            </React.Fragment>
        );
    };

    const header = (
        <div className="flex flex-column md:flex-row md:justify-content-between md:align-items-center">
            <h5 className="m-0">{loading ? "Carregando usuários" : "Usuários"}</h5>
            <span className="block mt-2 md:mt-0 p-input-icon-left">
                <i className="pi pi-search" />
                <InputText className="min-w-full" type="search" onInput={e => setGlobalFilter(e.currentTarget.value)} placeholder="Procurar..." />
            </span>
        </div>
    );

    const recordDialogFooter = (
        <>
            <Button label="Cancelar" icon="pi pi-times" disabled={loading} className="p-button-text text-red-500" onClick={hideDialog} />
            <Button label={loading ? "Salvando..." : "Salvar"} disabled={loading} icon="pi pi-check" className="p-button-text" onClick={saveRecord} />
        </>
    );

    React.useEffect(() => {
        loadRecords(true);
    }, []);

    return (
        <div className="grid crud-demo">
            <div className="col-12">
                <div className="card">
                    <Toast ref={toast} />
                    <Toolbar className="mb-4" left={leftToolbarTemplate}></Toolbar>

                    <DataTable
                        ref={dt}
                        value={records.filter(i => i.role !== "ROLE_MASTER")}
                        dataKey="id"
                        paginator
                        stripedRows={true}
                        rowHover
                        showGridlines
                        rows={10}
                        rowsPerPageOptions={[5, 10, 25]}
                        className="datatable-responsive"
                        paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                        currentPageReportTemplate="Mostrando {first} a {last} de {totalRecords} registros"
                        globalFilter={globalFilter}
                        emptyMessage="Nenhum registro encontrado"
                        header={header}
                        responsiveLayout="scroll">
                        <Column field="id" header="Matrícula" sortable body={codeBodyTemplate} headerStyle={{}}></Column>
                        <Column field="name" header="Nome" sortable body={nameBodyTemplate} headerStyle={{}}></Column>
                        <Column field="phone" header="Telefone" body={phoneBodyTemplate} sortable headerStyle={{}}></Column>
                        <Column field="role" header="Perfil" body={adminBodyTemplate} sortable headerStyle={{}}></Column>
                        <Column header="Ações" className="flex flex-row align-items-center justify-content-end" body={actionBodyTemplate}></Column>
                    </DataTable>

                    {/* Modal adicionar editar */}
                    <Dialog
                        visible={recordDialog}
                        style={{ width: "550px" }}
                        header={record?.id ? "Detalhes do Usuário" : "Novo Usuário"}
                        modal
                        className="p-fluid"
                        footer={recordDialogFooter}
                        onHide={hideDialog}>
                        <div className="flex flex-column md:flex-row ">
                            <div className="field col-12 md:col-12">
                                <label htmlFor="name">* Nome</label>
                                <InputText
                                    id="name"
                                    value={record?.name}
                                    onChange={e => onInputChange(e, "name")}
                                    required
                                    autoFocus
                                    className={classNames("p-3", { "p-invalid": submitted && !record?.name })}
                                />
                                {submitted && !record?.name && <small className="p-invalid">Nome é obrigatório.</small>}
                            </div>
                        </div>
                        <div className="flex flex-column md:flex-row ">
                            <div className="field col-12 md:col-12">
                                <label htmlFor="phone">* Telefone</label>
                                <InputText
                                    id="phone"
                                    value={record?.phone}
                                    onChange={e => onInputChange(e, "phone")}
                                    autoComplete={"new-password"}
                                    className={classNames("p-3", { "p-invalid": submitted && !record?.phone })}
                                />
                                {submitted && !record?.phone && <small className="p-invalid">Telefone é obrigatório.</small>}
                            </div>
                        </div>
                        <div className="flex flex-column md:flex-row ">
                            <div className="field col-12 md:col-12">
                                <label htmlFor="password">{record?.id ? "Senha" : "* Senha"}</label>
                                <InputText
                                    id="password"
                                    value={record?.password}
                                    type="password"
                                    onChange={e => onInputChange(e, "password")}
                                    autoComplete={"new-password"}
                                    className={classNames("p-3", { "p-invalid": submitted && !record?.password && !record?.id })}
                                />
                                {submitted && !record?.password && !record?.id && <small className="p-invalid">Senha é obrigatório.</small>}
                            </div>
                        </div>{" "}
                        <div className="flex flex-column md:flex-row ">
                            <div className="field col-12 md:col-12">
                                <label htmlFor="notes">Observação/CRM/CRO/etc</label>
                                <InputText
                                    id="notes"
                                    value={record?.notes}
                                    type="text"
                                    onChange={e => onInputChange(e, "notes")}
                                    autoComplete={"new-password"}
                                    className={classNames("p-3")}
                                />
                            </div>
                        </div>
                        <div className="flex flex-column md:flex-row ">
                            <div className="field col-12 md:col-12">
                                <label className="mb-3">* Perfil</label>
                                <div className="formgrid grid">
                                    <div className="field-radiobutton col-4">
                                        <RadioButton inputId="ADMIN" name="ADMIN" value={"ADMIN"} onChange={onRoleChange} checked={record?.role === "ADMIN"} />
                                        <label htmlFor="ADMIN">Administradores</label>
                                    </div>
                                    <div className="field-radiobutton col-4">
                                        <RadioButton
                                            inputId="DOCTOR"
                                            name="DOCTOR"
                                            value={"DOCTOR"}
                                            onChange={onRoleChange}
                                            checked={record?.role === "DOCTOR"}
                                        />
                                        <label htmlFor="DOCTOR">Médico</label>
                                    </div>
                                    <div className="field-radiobutton col-4">
                                        <RadioButton inputId="NURSE" name="NURSE" value={"NURSE"} onChange={onRoleChange} checked={record?.role === "NURSE"} />
                                        <label htmlFor="NURSE">Enfermeiro(a)</label>
                                    </div>
                                    <div className="field-radiobutton col-4">
                                        <RadioButton
                                            inputId="DENTIST"
                                            name="DENTIST"
                                            value={"DENTIST"}
                                            onChange={onRoleChange}
                                            checked={record?.role === "DENTIST"}
                                        />
                                        <label htmlFor="DENTIST">Dentista</label>
                                    </div>
                                    <div className="field-radiobutton col-4">
                                        <RadioButton
                                            inputId="PHARMACEUTICAL"
                                            name="PHARMACEUTICAL"
                                            value={"PHARMACEUTICAL"}
                                            onChange={onRoleChange}
                                            checked={record?.role === "PHARMACEUTICAL"}
                                        />
                                        <label htmlFor="PHARMACEUTICAL">Farmacêutico</label>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </Dialog>
                </div>
            </div>
        </div>
    );
};

export default Crud;
